import { z } from 'zod';

import { AreaIdSchema } from './area.schema';
import {
  AreaTaxonomySchema,
  ApiSortDirSchema,
  DEFAULT_AREA_TAXONOMY,
  DEFAULT_TAXONOMY,
  SortDirSchema,
  type FilterKey,
  type SortDir,
  type SortKey,
  type WindowKey,
  WindowKeySchema,
  FilterKeySchema,
  SortKeySchema,
  TaxonomySchema,
} from './enums';

/* `apps/web` needs these to strip default-valued params from a shared link and
   to render active sort state, so they are public rather than module-private. */
export const DEFAULT_WINDOW: WindowKey = '1Y';
export const DEFAULT_FILTER: FilterKey = 'ALL';
export const DEFAULT_SORT_A = Object.freeze({ k: 'ret', d: -1 }) as {
  readonly k: SortKey;
  readonly d: SortDir;
};
export const DEFAULT_SORT_B = Object.freeze({ k: 'risk', d: 1 }) as {
  readonly k: SortKey;
  readonly d: SortDir;
};

/** SPA URL: unknown enum → product default (FR-A2 deep links). */
const WindowSearchParamSchema = WindowKeySchema.default(DEFAULT_WINDOW).catch(DEFAULT_WINDOW);
const FilterSearchParamSchema = FilterKeySchema.default(DEFAULT_FILTER).catch(DEFAULT_FILTER);
const TaxonomySearchParamSchema = TaxonomySchema.default(DEFAULT_TAXONOMY).catch(DEFAULT_TAXONOMY);
const AreaTaxonomySearchParamSchema =
  AreaTaxonomySchema.default(DEFAULT_AREA_TAXONOMY).catch(DEFAULT_AREA_TAXONOMY);

/**
 * Neutral sort state (no baked-in catch). Apply .default().catch() at the
 * field site so sortA and sortB keep independent fallbacks.
 */
export const SortStateSchema = z.object({
  k: SortKeySchema,
  d: z.preprocess((v) => (typeof v === 'string' && v.trim() !== '' ? Number(v) : v), SortDirSchema),
});
export type SortState = z.infer<typeof SortStateSchema>;

/* Thunks, not the shared literal: `.catch(obj)` hands back the SAME object on
   every invalid parse, so one mutation of a caught result would rewrite the
   module default for the whole process — and `.default()` clones from that same
   live object, corrupting both arms. */
const sortDefault = (base: { readonly k: SortKey; readonly d: SortDir }) => (): SortState => ({
  ...base,
});

/**
 * SPA URL search params for `/` (dashboard). Short names on purpose (ADR-0003);
 * mapping to API long names happens once in `apps/web/src/data/queries/*`.
 */
export const DashboardSearchParamsSchema = z.object({
  win: WindowSearchParamSchema,
  idx: FilterSearchParamSchema,
  /* Parsed and defaulted here in PR1; no `apps/web` route reads it until the
     industry grouping ships. Present now so a link shared from a later build
     round-trips through this one instead of being dropped. */
  tax: TaxonomySearchParamSchema,
  sortA: SortStateSchema.default(sortDefault(DEFAULT_SORT_A)).catch(sortDefault(DEFAULT_SORT_A)),
  sortB: SortStateSchema.default(sortDefault(DEFAULT_SORT_B)).catch(sortDefault(DEFAULT_SORT_B)),
});
export type DashboardSearchParams = z.infer<typeof DashboardSearchParamsSchema>;

/** SPA URL search params for `/sector/$sectorId`. */
export const SectorSearchParamsSchema = z.object({
  win: WindowSearchParamSchema,
  idx: FilterSearchParamSchema,
});
export type SectorSearchParams = z.infer<typeof SectorSearchParamsSchema>;

/** SPA URL search params for `/area/$areaId`; v1 areas are industries. */
export const AreaSearchParamsSchema = z.object({
  win: WindowSearchParamSchema,
  idx: FilterSearchParamSchema,
  tax: AreaTaxonomySearchParamSchema,
});
export type AreaSearchParams = z.infer<typeof AreaSearchParamsSchema>;

/**
 * SPA URL search params for `/stock/$ticker`.
 *
 * `range` is chart-local (F12) and OPTIONAL: unset means "follow the global
 * window", which the reference implementation expresses as `S.range || w`
 * (demo:633, :1004). Defaulting it to a concrete window would make the unset
 * state inexpressible and render `?win=3Y` as a 3Y header over a 1Y chart.
 * Resolve as `range ?? win` at the route/hook.
 */
export const StockSearchParamsSchema = z.object({
  win: WindowSearchParamSchema,
  idx: FilterSearchParamSchema,
  range: WindowKeySchema.optional().catch(undefined),
  tax: TaxonomySchema.optional().catch(undefined),
  area: AreaIdSchema.optional().catch(undefined),
});
export type StockSearchParams = z.infer<typeof StockSearchParamsSchema>;

export const DEFAULT_API_LIMIT = 50;
/**
 * Must be >= the largest universe the plan admits, or the §6.6 sort-key parity
 * test (`limit=<universeSize>&sort=risk`) fails once the universe passes it.
 */
export const API_LIMIT_MAX = 2_500;
export const API_Q_MAX = 128;

/**
 * API query vocabulary (plan §6.6 / naming §4.2): long names only.
 * Never accept `win`/`idx` here — that mapping is the web query-hook layer.
 *
 * Uses `.default()` for omitted keys (ETag canonicalQuery) but **no** `.catch()`:
 * hand-crafted invalid values → parse failure → 400 VALIDATION_FAILED (data-flow §1.1).
 * SPA deep-link soft defaults live only on URL search-param schemas above.
 */
const apiQueryFields = {
  window: WindowKeySchema.default(DEFAULT_WINDOW),
  index: FilterKeySchema.default(DEFAULT_FILTER),
  sort: SortKeySchema.default('ret'),
  dir: ApiSortDirSchema.default('desc'),
  limit: z.coerce.number().int().positive().max(API_LIMIT_MAX).default(DEFAULT_API_LIMIT),
  q: z.string().max(API_Q_MAX).default(''),
} as const;

/**
 * Per-route query schemas (plan §6.6 assigns a distinct set per route).
 *
 * These are not cosmetic: `canonicalQuery` is the parsed object with keys sorted,
 * and it keys BOTH the ETag `routeKey` and the Query cache. A single permissive
 * schema lets `/v1/dashboard?sort=mcap` parse and mint a DIFFERENT ETag for a
 * byte-identical body — and injects a `limit` into a payload that is documented
 * as the full filtered set, not paginated.
 */
export const DashboardQuerySchema = z.object({
  window: apiQueryFields.window,
  index: apiQueryFields.index,
});
export type DashboardQuery = z.infer<typeof DashboardQuerySchema>;

/** `/v1/sectors` and `/v1/sectors/:id`. */
export const SectorQuerySchema = DashboardQuerySchema;
export type SectorQuery = z.infer<typeof SectorQuerySchema>;

/** `/v1/areas` and `/v1/areas/:id`; unsupported taxonomies are validation errors. */
export const AreaQuerySchema = z.object({
  window: apiQueryFields.window,
  index: apiQueryFields.index,
  taxonomy: AreaTaxonomySchema.default(DEFAULT_AREA_TAXONOMY),
});
export type AreaQuery = z.infer<typeof AreaQuerySchema>;

/** `/v1/securities` — the only sorted, limited collection route. */
export const SecuritiesQuerySchema = z.object({
  window: apiQueryFields.window,
  index: apiQueryFields.index,
  sort: apiQueryFields.sort,
  dir: apiQueryFields.dir,
  limit: apiQueryFields.limit,
});
export type SecuritiesQuery = z.infer<typeof SecuritiesQuerySchema>;

/** `/v1/search`. */
export const SearchQuerySchema = z.object({
  q: apiQueryFields.q,
  limit: apiQueryFields.limit,
});
export type SearchQuery = z.infer<typeof SearchQuerySchema>;

/** `/v1/securities/:ticker/{series,peers,diversifiers}`. */
export const SeriesQuerySchema = z.object({ window: apiQueryFields.window });
export type SeriesQuery = z.infer<typeof SeriesQuerySchema>;

/**
 * Union of every API query field. Retained for callers that need the full
 * vocabulary; prefer the per-route schema above so the ETag key set matches
 * the route's actual inputs.
 */
export const ApiQueryParamsSchema = z.object(apiQueryFields);
export type ApiQueryParams = z.infer<typeof ApiQueryParamsSchema>;
